/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx, css } from '@emotion/react'

import { EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiIcon, EuiLink, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import LandingPageOuterContainer from '@modules/access-management-components/LandingPageOuterContainer'
import LandingPageInnerContainer from '@modules/access-management-components/LandingPageInnerContainer'
import { DocLink } from '@modules/doclinks'

import type { FunctionComponent } from 'react'

const AWSPrepaidLandingPage: FunctionComponent = () => (
  <LandingPageOuterContainer>
    <LandingPageInnerContainer panelCssOverride={css({ padding: 0 })}>
      <EuiEmptyPrompt
        title={
          <EuiFlexGroup direction='column' alignItems='center'>
            <EuiFlexItem>
              <EuiIcon type='checkInCircleFilled' color='success' size='xxl' />
            </EuiFlexItem>
            <EuiFlexItem>
              <FormattedMessage
                id='aws-marketplace-signup-modal.prepaid-landing-page.title'
                defaultMessage='Annual subscription registered successfully'
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        }
        body={
          <EuiFlexGroup direction='column' alignItems='center'>
            <EuiFlexItem>
              <EuiText textAlign='center'>
                <FormattedMessage
                  id='aws-marketplace-signup-modal.prepaid-landing-page.subtitle-1'
                  defaultMessage='Thank you for choosing '
                />
                <EuiLink href='https://www.elastic.co/cloud' target='_blank'>
                  Elastic Cloud
                </EuiLink>
                <FormattedMessage
                  id='aws-marketplace-signup-modal.prepaid-landing-page.subtitle-2'
                  defaultMessage=' through the AWS Marketplace.'
                />
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiText textAlign='center'>
                <FormattedMessage
                  id='aws-marketplace-signup-modal.prepaid-landing-page.description'
                  defaultMessage='Your subscription will automatically activate on your contract start date and usage will begin to draw from your prepaid balance.'
                />
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiText textAlign='center'>
                <strong>
                  <FormattedMessage
                    id='aws-marketplace-signup-modal.prepaid-landing-page.no-action-required'
                    defaultMessage='No further action is required on your part.'
                  />
                </strong>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        }
        footer={
          <EuiText size='s' textAlign='center'>
            <strong>
              <FormattedMessage
                id='aws-marketplace-signup-modal.prepaid-landing-page.learn-more'
                defaultMessage='Want to learn more ? '
              />
            </strong>
            <DocLink link='billingPrepaidConsumptionModel'>
              <FormattedMessage
                id='aws-marketplace-signup-modal.prepaid-landing-page.explore-prepaid-consumption'
                defaultMessage='Explore prepaid consumption model'
              />
            </DocLink>
          </EuiText>
        }
      />
    </LandingPageInnerContainer>
  </LandingPageOuterContainer>
)

export default AWSPrepaidLandingPage
